import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 验证码 Hook 的返回值接口
 */
export interface UseCaptchaReturn {
  /** Canvas ref,需要绑定到 canvas 元素 */
  canvasRef: React.RefObject<HTMLCanvasElement>;
  /** 用户输入的值 */
  inputValue: number;
  /** 设置用户输入值 */
  setInputValue: (value: number) => void;
  /** 刷新验证码 */
  refresh: () => void;
  /** 获取当前正确答案的函数(避免因定时器改变导致的引用问题) */
  getCurrentAnswer: () => number;
}

/**
 * 验证码 Hook 配置参数
 */
export interface UseCaptchaOptions {
  /** 验证码刷新间隔(毫秒),默认 60000ms (1分钟) */
  refreshInterval?: number;
  /** 验证结果变化时的回调 */
  onValidChange?: (isValid: boolean) => void;
}

/**
 * 验证码 Hook
 * 用于管理验证码的生成、刷新、用户输入和验证逻辑
 *
 * @param options - 配置参数
 * @returns 验证码相关的状态和方法
 */
export function useCaptcha(options: UseCaptchaOptions = {}): UseCaptchaReturn {
  const { refreshInterval = 60000, onValidChange } = options;

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // 使用 ref 存储当前正确答案,避免因 state 更新导致的重渲染
  const currentAnswerRef = useRef<number>(0);

  // 用户输入的值
  const [inputValue, setInputValue] = useState<number>();

  // 验证结果:用户输入是否与当前答案一致
  const [isValid, setIsValid] = useState<boolean>(false);

  /**
   * 生成并绘制验证码
   * 随机生成两个 20 以内的整数相加
   */
  const generateCaptcha = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 生成两个 0-20 之间的随机整数
    const num1 = Math.floor(Math.random() * 21);
    const num2 = Math.floor(Math.random() * 21);
    const answer = num1 + num2;

    // 更新当轮正确答案(使用 ref 存储)
    currentAnswerRef.current = answer;

    // 清空画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 设置背景色
    ctx.fillStyle = '#f0f2f5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 绘制干扰线
    for (let i = 0; i < 3; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // 绘制验证码文本
    const text = `${num1} + ${num2} = ?`;
    ctx.font = 'bold 20px Arial';
    ctx.fillStyle = '#165dff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 添加文字阴影效果
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    // 绘制干扰点
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
      ctx.beginPath();
      ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, []);

  // 组件挂载时生成验证码
  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  // 定时刷新验证码
  useEffect(() => {
    const timer = setInterval(() => {
      generateCaptcha();
      // 刷新时清空用户输入
      setInputValue(undefined);
    }, refreshInterval);

    return () => {
      clearInterval(timer);
    };
  }, [generateCaptcha, refreshInterval]);

  // 当用户输入变化时,自动验证
  useEffect(() => {
    // 检查输入是否为空
    if (!inputValue) {
      setIsValid(false);
      return;
    }

    // 转换为数字并比较

    // 检查是否为有效数字
    if (isNaN(inputValue)) {
      setIsValid(false);
      return;
    }

    // 比较答案(从 ref 中获取最新值)
    const valid = inputValue === currentAnswerRef.current;
    setIsValid(valid);
  }, [inputValue]);

  // 验证结果变化时触发回调
  useEffect(() => {
    if (onValidChange) {
      onValidChange(isValid);
    }
  }, [isValid, onValidChange]);

  /**
   * 刷新验证码
   * 同时清空用户输入
   */
  const refresh = useCallback(() => {
    generateCaptcha();
    setInputValue(undefined);
  }, [generateCaptcha]);

  /**
   * 获取当前正确答案
   * 返回函数而非值引用,确保总是获取到最新的答案
   */
  const getCurrentAnswer = useCallback(() => {
    return currentAnswerRef.current;
  }, []);

  return {
    canvasRef,
    inputValue,
    setInputValue,
    refresh,
    getCurrentAnswer,
  };
}
